import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { create, update } from '../../../api/ventura/location';
import { draftLocation, Location } from '../../../constants/locationTable';
import useAdGroupLocations from '../../../hooks/useAdGroupLocations';
import { useConfig } from '../../../hooks/useConfig';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { isBlank } from '../../../util/helpers';
import Checkbox from '../../forms/Checkbox';
import FormEndOptions from '../../forms/FormEndOptions';
import NumericTextField from '../../forms/NumericTextField';
import TextField from '../../forms/TextField';
import TextFieldArray from '../../forms/TextFieldArray';
import ServerErrorAlert from '../../shared/ServerErrorAlert';
import AutocompleteLocationField from './AutocompleteLocationField';

interface LocationFormProps {
  location: Location;
  adGroupId: number;
  adGroupName: string;
  onClose: () => void;
  readOnly?: boolean;
}

const controller = new AbortController();
const { signal } = controller;

function LocationForm({ location, adGroupId, adGroupName, onClose, readOnly = false }: LocationFormProps) {
  const form = useForm({
    defaultValues:
      (location && {
        ...location,
        id: location.id?.toString() || '',
        latitude: location.latitude?.toString() || '',
        longitude: location.longitude?.toString() || '',
        poi_id: location.poi_id?.toString() || '',
        radius: location.radius?.toString() || '',
        variables: location.variables?.map((v) => ({ value: v })),
      }) ||
      draftLocation,
  });
  const { apiHost } = useConfig();
  const { showSnackBar } = useSnackBar();

  const { refetch } = useAdGroupLocations(adGroupId);
  const saveLocation = useMutation({
    mutationFn: (data: any) => {
      const payload = {
        ...data,
        latitude: isBlank(data.latitude) ? null : data.latitude,
        longitude: isBlank(data.longitude) ? null : data.longitude,
        radius: isBlank(data.radius) ? null : data.radius,
      };
      if (data.id) {
        return update(apiHost, adGroupId, payload, signal);
      }
      return create(apiHost, adGroupId, payload, signal);
    },
    onSuccess: (data) => {
      if (!data.errors) {
        onClose();
        showSnackBar('Location saved successfully!', 'success', {
          event: 'location_saved',
          ad_group_id: adGroupId,
          location_id: data.id,
        });
        refetch();
      }
    },
    onError: () => {
      showSnackBar('Failed to save location', 'error', {
        event: 'location_save_error',
        ad_group_id: adGroupId,
      });
    },
  });

  const submitHandler = (data: any) => {
    saveLocation.mutate({ ...data, variables: data.variables?.map((v: { value: string }) => v.value) });
  };

  const handleCancelBtnClick = () => {
    controller.abort();
    onClose();
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitHandler)}>
        <Stack alignItems="flex-start" spacing={2} padding={2}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {location?.id ? 'Edit' : 'Add'} Location for {adGroupName}
          </Typography>
          <TextField
            name="name"
            label="Name"
            fullWidth
            autoFocus
            rules={{
              required: 'Please enter a value.',
            }}
            disabled={readOnly}
          />
          <TextField name="address" label="Address" fullWidth autoComplete="street-address" disabled={readOnly} />
          <Stack direction="row" spacing={1} justifyContent="stretch" width="100%">
            <NumericTextField name="latitude" label="Latitude" allowNegative={false} fullWidth disabled={readOnly} />
            <NumericTextField name="longitude" label="Longitude" allowNegative fullWidth disabled={readOnly} />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="stretch" width="100%">
            <NumericTextField
              name="poi_id"
              label="POI ID"
              allowNegative={false}
              decimalScale={0}
              fullWidth
              autoComplete="on"
              disabled={readOnly}
            />
            <NumericTextField
              name="radius"
              label="Radius"
              allowNegative={false}
              decimalScale={0}
              fullWidth
              autoComplete="on"
              disabled={readOnly}
            />
            <NumericTextField
              name="zipcode"
              label="Zip Code"
              allowNegative={false}
              decimalScale={0}
              fullWidth
              autoComplete="postal-code"
              disabled={readOnly}
            />
          </Stack>
          <TextField name="street" label="Street" fullWidth autoComplete="street-address" disabled={readOnly} />
          <TextField name="location_type" label="Type" fullWidth disabled={readOnly} />
          <AutocompleteLocationField
            label="State"
            name="state"
            locationType="state"
            sx={{ width: '100%' }}
            autoHighlight
            disabled={readOnly}
          />
          <AutocompleteLocationField
            label="City"
            name="city"
            locationType="city"
            sx={{ width: '100%' }}
            autoHighlight
            disabled={readOnly}
          />
          <AutocompleteLocationField
            label="DMA"
            name="dma"
            locationType="dma"
            sx={{ width: '100%' }}
            autoHighlight
            disabled={readOnly}
          />
          <TextField name="congressional_district" label="Cong. District" fullWidth disabled={readOnly}/>
          <Checkbox name="preceptivity_disabled" label="Disable Preceptivity" disabled={readOnly}/>
          <TextFieldArray name="variables" label="Variable" disabled={readOnly}/>
        </Stack>
        <FormEndOptions>
          <Stack spacing={2}>
            {saveLocation.isSuccess && saveLocation.data.errors && (
              <ServerErrorAlert errors={saveLocation.data.errors} />
            )}
            <Stack direction="row" justifyContent="center" width="100%" spacing={2}>
              <Button variant="outlined" onClick={handleCancelBtnClick}>
                Cancel
              </Button>
              {!readOnly && (
                <Button
                  type="submit"
                  disabled={saveLocation.isLoading}
                  endIcon={saveLocation.isLoading ? <CircularProgress size="1rem" /> : null}
                >
                  Save
                </Button>
              )}
            </Stack>
          </Stack>
        </FormEndOptions>
      </form>
    </FormProvider>
  );
}

export default LocationForm;
